import React, { useState, useEffect } from 'react';
import axios from 'axios';

const IntakeForm = ({ journeyId, token, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    problem_statement: '',
    business_impact: '',
    scope: '',
    constraints: '',
    initial_hypotheses: [''],
    research_completed: false,
    research_notes: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [existingIntake, setExistingIntake] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmEdit, setShowConfirmEdit] = useState(false);

  useEffect(() => {
    loadExistingIntake();
  }, [journeyId, token]);

  const loadExistingIntake = async () => {
    try {
      const response = await axios.get(
        `http://localhost:5000/api/journeys/${journeyId}/intake`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.intake) {
        setExistingIntake(response.data.intake);
        setFormData({
          problem_statement: response.data.intake.problem_statement || '',
          business_impact: response.data.intake.business_impact || '',
          scope: response.data.intake.scope || '',
          constraints: response.data.intake.constraints || '',
          initial_hypotheses: response.data.intake.initial_hypotheses || [''],
          research_completed: response.data.intake.research_completed || false,
          research_notes: response.data.intake.research_notes || '',
        });
      }
    } catch (err) {
      console.error('Load existing intake error:', err);
    }
  };

  // Validate field lengths
  const validateField = (name, value) => {
    const newErrors = { ...errors };

    if (name === 'problem_statement') {
      if (value.length < 20) {
        newErrors.problem_statement = `Minimum 20 characters (${value.length}/20)`;
      } else if (value.length > 2000) {
        newErrors.problem_statement = 'Maximum 2000 characters';
      } else {
        delete newErrors.problem_statement;
      }
    }

    if (name === 'business_impact') {
      if (value.length < 10) {
        newErrors.business_impact = `Minimum 10 characters (${value.length}/10)`;
      } else if (value.length > 2000) {
        newErrors.business_impact = 'Maximum 2000 characters';
      } else {
        delete newErrors.business_impact;
      }
    }

    if (name === 'scope') {
      if (value.length < 10) {
        newErrors.scope = `Minimum 10 characters (${value.length}/10)`;
      } else if (value.length > 2000) {
        newErrors.scope = 'Maximum 2000 characters';
      } else {
        delete newErrors.scope;
      }
    }

    if (name === 'constraints' && value.length > 2000) {
      newErrors.constraints = 'Maximum 2000 characters';
    } else if (name === 'constraints') {
      delete newErrors.constraints;
    }

    if (name === 'research_notes' && value.length > 2000) {
      newErrors.research_notes = 'Maximum 2000 characters';
    } else if (name === 'research_notes') {
      delete newErrors.research_notes;
    }

    setErrors(newErrors);
  };

  // Handle text input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  // Handle hypothesis changes
  const handleHypothesisChange = (index, value) => {
    const newHypotheses = [...formData.initial_hypotheses];
    newHypotheses[index] = value;
    setFormData({ ...formData, initial_hypotheses: newHypotheses });
  };

  // Add new hypothesis field
  const addHypothesis = () => {
    if (formData.initial_hypotheses.length < 10) {
      setFormData({
        ...formData,
        initial_hypotheses: [...formData.initial_hypotheses, ''],
      });
    }
  };

  // Remove hypothesis field
  const removeHypothesis = (index) => {
    if (formData.initial_hypotheses.length > 1) {
      const newHypotheses = formData.initial_hypotheses.filter(
        (_, i) => i !== index
      );
      setFormData({ ...formData, initial_hypotheses: newHypotheses });
    }
  };

  // Handle research completed checkbox
  const handleCheckboxChange = (e) => {
    const { checked } = e.target;
    setFormData({ ...formData, research_completed: checked });
  };

  // Check if form is valid
  const isFormValid = () => {
    const hasNonEmptyHypotheses = formData.initial_hypotheses.some(
      (h) => h.trim().length > 0
    );
    return (
      formData.problem_statement.trim().length >= 20 &&
      formData.business_impact.trim().length >= 10 &&
      formData.scope.trim().length >= 10 &&
      hasNonEmptyHypotheses &&
      Object.keys(errors).length === 0
    );
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      // Filter out empty hypotheses
      const hypothesesList = formData.initial_hypotheses.filter(
        (h) => h.trim().length > 0
      );

      const payload = {
        problem_statement: formData.problem_statement.trim(),
        business_impact: formData.business_impact.trim(),
        scope: formData.scope.trim(),
        constraints: formData.constraints.trim() || null,
        initial_hypotheses: hypothesesList,
        research_completed: formData.research_completed,
        research_notes: formData.research_notes.trim() || null,
      };

      const response = await axios.post(
        `http://localhost:5000/api/journeys/${journeyId}/intake`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setSuccessMessage(
        'Intake submitted successfully! Journey can now proceed to ideation.'
      );
      setExistingIntake(response.data.intake);
      setIsEditing(false);
      if (onSuccess) {
        setTimeout(() => onSuccess(response.data.intake), 1500);
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to submit intake. Please try again.';
      setErrorMessage(errorMsg);
      console.error('Intake submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Clear form
  const handleClear = () => {
    setFormData({
      problem_statement: '',
      business_impact: '',
      scope: '',
      constraints: '',
      initial_hypotheses: [''],
      research_completed: false,
      research_notes: '',
    });
    setErrors({});
    setErrorMessage('');
    setSuccessMessage('');
  };

  // Read-only view for completed intake
  if (existingIntake && !isEditing) {
    return (
      <div className="form-container">
        <div className="form-card" style={{ backgroundColor: '#f9f9f9', opacity: 0.95 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h2 className="form-title">Innovation Journey - Intake Form</h2>
              <div style={{
                display: 'inline-block',
                backgroundColor: '#d4edda',
                color: '#155724',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '13px',
                fontWeight: '600',
                marginTop: '8px',
              }}>
                ✓ COMPLETE
              </div>
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => setShowConfirmEdit(true)}
              style={{ padding: '8px 16px', fontSize: '13px' }}
            >
              ✏️ Edit
            </button>
          </div>

          {successMessage && <div className="form-success">{successMessage}</div>}
          {errorMessage && <div className="form-error">{errorMessage}</div>}

          {showConfirmEdit && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }} onClick={() => setShowConfirmEdit(false)}>
              <div style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '8px',
                maxWidth: '400px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
              }} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ marginBottom: '12px', color: '#2c3e50' }}>Edit Completed Intake?</h3>
                <p style={{ color: '#666', marginBottom: '20px' }}>
                  This intake has already been marked complete. Changes will be logged in the audit trail.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={() => setShowConfirmEdit(false)}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setIsEditing(true);
                      setShowConfirmEdit(false);
                    }}
                  >
                    Continue Editing
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: '20px' }}>
            {formData.problem_statement && (
              <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Problem Statement</h4>
                <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.problem_statement}</p>
              </div>
            )}
            {formData.business_impact && (
              <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Business Impact</h4>
                <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.business_impact}</p>
              </div>
            )}
            {formData.scope && (
              <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Scope</h4>
                <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.scope}</p>
              </div>
            )}
            {formData.constraints && (
              <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e0e0e0' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Constraints</h4>
                <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.constraints}</p>
              </div>
            )}
            {formData.research_notes && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '8px', fontWeight: '600' }}>Research Notes</h4>
                <p style={{ color: '#555', whiteSpace: 'pre-wrap', margin: '0' }}>{formData.research_notes}</p>
              </div>
            )}
          </div>

          <div className="form-actions">
            {onCancel && <button className="btn btn-secondary" onClick={onCancel}>Back</button>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-card">
        <h2>Innovation Journey - Intake Form</h2>
        <p className="form-subtitle">Step 1 of 5: Define the Problem</p>

        {errorMessage && <div className="error-banner">{errorMessage}</div>}
        {successMessage && (
          <div className="success-banner">{successMessage}</div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Problem Statement */}
          <div className="form-group">
            <label htmlFor="problem_statement">
              Problem Statement <span className="required">*</span>
            </label>
            <textarea
              id="problem_statement"
              name="problem_statement"
              value={formData.problem_statement}
              onChange={handleInputChange}
              placeholder="Describe the problem or opportunity you're addressing (min 20 chars)"
              rows="4"
              className={errors.problem_statement ? 'input-error' : ''}
            />
            <div className="character-counter">
              {formData.problem_statement.length}/2000
            </div>
            {errors.problem_statement && (
              <div className="error-text">{errors.problem_statement}</div>
            )}
          </div>

          {/* Business Impact */}
          <div className="form-group">
            <label htmlFor="business_impact">
              Business Impact <span className="required">*</span>
            </label>
            <textarea
              id="business_impact"
              name="business_impact"
              value={formData.business_impact}
              onChange={handleInputChange}
              placeholder="What's the business impact? Revenue, customer satisfaction, market share..."
              rows="4"
              className={errors.business_impact ? 'input-error' : ''}
            />
            <div className="character-counter">
              {formData.business_impact.length}/2000
            </div>
            {errors.business_impact && (
              <div className="error-text">{errors.business_impact}</div>
            )}
          </div>

          {/* Scope */}
          <div className="form-group">
            <label htmlFor="scope">
              Scope <span className="required">*</span>
            </label>
            <textarea
              id="scope"
              name="scope"
              value={formData.scope}
              onChange={handleInputChange}
              placeholder="What's included and excluded from this initiative?"
              rows="3"
              className={errors.scope ? 'input-error' : ''}
            />
            <div className="character-counter">{formData.scope.length}/2000</div>
            {errors.scope && <div className="error-text">{errors.scope}</div>}
          </div>

          {/* Constraints */}
          <div className="form-group">
            <label htmlFor="constraints">Constraints</label>
            <textarea
              id="constraints"
              name="constraints"
              value={formData.constraints}
              onChange={handleInputChange}
              placeholder="Budget, timeline, technical, organizational constraints..."
              rows="3"
              className={errors.constraints ? 'input-error' : ''}
            />
            <div className="character-counter">
              {formData.constraints.length}/2000
            </div>
            {errors.constraints && (
              <div className="error-text">{errors.constraints}</div>
            )}
          </div>

          {/* Initial Hypotheses */}
          <div className="form-group">
            <label>
              Initial Hypotheses <span className="required">*</span>
              <span className="hint">
                (min 1, max 10)
              </span>
            </label>
            <div className="hypotheses-list">
              {formData.initial_hypotheses.map((hypothesis, index) => (
                <div key={index} className="hypothesis-item">
                  <input
                    type="text"
                    value={hypothesis}
                    onChange={(e) =>
                      handleHypothesisChange(index, e.target.value)
                    }
                    placeholder={`e.g., Faster load times will reduce churn by 25%`}
                    maxLength="500"
                  />
                  {formData.initial_hypotheses.length > 1 && (
                    <button
                      type="button"
                      className="btn-remove-hypothesis"
                      onClick={() => removeHypothesis(index)}
                      title="Remove this hypothesis"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            {formData.initial_hypotheses.length < 10 && (
              <button
                type="button"
                className="btn-add-hypothesis"
                onClick={addHypothesis}
              >
                + Add Hypothesis
              </button>
            )}
          </div>

          {/* Research Completed */}
          <div className="form-group checkbox-group">
            <label htmlFor="research_completed">
              <input
                type="checkbox"
                id="research_completed"
                checked={formData.research_completed}
                onChange={handleCheckboxChange}
              />
              Research has been completed
            </label>
          </div>

          {/* Research Notes (conditional) */}
          {formData.research_completed && (
            <div className="form-group">
              <label htmlFor="research_notes">Research Notes</label>
              <textarea
                id="research_notes"
                name="research_notes"
                value={formData.research_notes}
                onChange={handleInputChange}
                placeholder="User interviews, analytics review, competitor analysis..."
                rows="3"
                className={errors.research_notes ? 'input-error' : ''}
              />
              <div className="character-counter">
                {formData.research_notes.length}/2000
              </div>
              {errors.research_notes && (
                <div className="error-text">{errors.research_notes}</div>
              )}
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel || handleClear}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClear}
              disabled={loading}
            >
              Clear
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isFormValid() || loading}
            >
              {loading ? 'Submitting...' : 'Submit Intake'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IntakeForm;
